/**
 * Configurable extra-detail catalog for the customer map's site list. A sidebar
 * row always shows a name, a status, the site's description and its alarms;
 * beyond that the user chooses which optional per-site details ride along
 * (loads, address, ICCID, coordinates) and saves that choice as a personal
 * default. This module is the single source of which fields exist, how each is
 * labelled (loads follow the customer's relay-load term), and how each reads
 * its display value off a map site row.
 *
 * The field ids are shared with the legacy plant map and structure-map
 * configure lists. The saved choice belongs to a user and a customer type
 * together, since which details are worth showing follows the customer's
 * CustomerType.
 *
 * Contains:
 * - PREFERENCE_BASE_KEY / PREFERENCE_COLUMNS_SUFFIX / CONFIGURABLE_FIELDS /
 *   DEFAULT_SHOWN_FIELDS
 * - knownFields — drop field ids a saved preference names but the catalog no longer offers
 * - fieldLabel  — localized label (tenant-term aware for loads)
 * - fieldValue  — display string for a field on a given site
 *
 * Returns plain JS — no JSX.
 */

// odl-app user-preference key parts for the saved column selection; the whole
// key comes from useScopedPreferenceKey, which scopes it to the customer type.
export const PREFERENCE_BASE_KEY = 'map.siteGroupList';
export const PREFERENCE_COLUMNS_SUFFIX = 'columns';

export const CONFIGURABLE_FIELDS = Object.freeze([
  Object.freeze({ id: 'relay_load_names', isRelayLoadTerm: true }),
  Object.freeze({ id: 'location_description', labelKey: 'map.address', defaultLabel: 'Address' }),
  Object.freeze({ id: 'iccid', labelKey: 'map.iccid', defaultLabel: 'ICCID' }),
  Object.freeze({ id: 'latLng', labelKey: 'map.coords', defaultLabel: 'Coords' }),
]);

export const DEFAULT_SHOWN_FIELDS = Object.freeze(['relay_load_names']);

const FIELD_IDS = new Set(CONFIGURABLE_FIELDS.map(field => field.id));

/**
 * Keep only the field ids this catalog still offers. A saved preference is
 * years of user choices the map does not control — it can name a field the
 * catalog has since dropped, and a row must ignore that rather than render a
 * nameless line.
 *
 * @param {string[]} fieldIds - field ids from a saved user preference.
 * @returns {string[]} the subset the catalog recognizes, in the saved order.
 */
export function knownFields(fieldIds) {
  return (fieldIds ?? []).filter(fieldId => FIELD_IDS.has(fieldId));
}

/**
 * Localized field label. The loads field follows the customer's relay-load term
 * ("Loads" / "Panels" / "Zones"); the rest are plain i18n keys.
 *
 * @param {{ id: string, isRelayLoadTerm?: boolean, labelKey?: string, defaultLabel?: string }} field
 * @param {{ t: Function, relayLoadTermPlural: string }} deps
 * @returns {string}
 */
export function fieldLabel(field, { t, relayLoadTermPlural }) {
  if (field.isRelayLoadTerm) return relayLoadTermPlural;
  return t(field.labelKey, field.defaultLabel);
}

/**
 * Display string for one configurable field on a site row. Missing detail data
 * yields an empty string (the row renders the "none" placeholder).
 *
 * @param {string} fieldId
 * @param {object} site
 * @returns {string}
 */
export function fieldValue(fieldId, site) {
  switch (fieldId) {
    case 'relay_load_names':
      return (site.loads ?? [])
        .map(load => load.name)
        .filter(Boolean)
        .join(', ');
    case 'location_description':
      return site.address ?? '';
    case 'iccid':
      return site.iccid ?? '';
    case 'latLng':
      return site.latitude != null && site.longitude != null ? `${site.latitude}, ${site.longitude}` : '';
    default:
      return '';
  }
}
